import React, { useState } from "react"
import useTitle from "@/hooks/useTitle"
import { addIcon } from "@/assets/img/imgAssets"
import {
  PlusIcon,
  MinusIcon,
  TrashIcon,
  PencilSquareIcon,
  ClipboardDocumentIcon,
} from "@heroicons/react/24/outline"
import PulseLoader from "react-spinners/PulseLoader"

import { useGetUserSchedulingPagesQuery } from "@/features/userSchedulingPages/userSchedulingPagesApiSlice"

const EtUserSchedulingPageList = ({
  onAdd,
  onUpdate,
  onDelete,
}) => {

  // First Created - 06 May 2026
  // Author - Atul Pandey
  // Purpose - Admin list page for Trinity SlotBoard scheduling pages.

  useTitle("SlotBoard Pages")

  const [showDetails, setShowDetails] = useState({})

  const {
    data: userSchedulingPagesData,
    isError,
    error,
    isLoading,
  } = useGetUserSchedulingPagesQuery()

  const handleToggleDetails = (pageId) => {
    setShowDetails(prev => ({
      ...prev,
      [pageId]: !prev[pageId],
    }))
  }

  const handleCopyPublicUrl = async (publicSlug) => {
    const publicUrl = `${window.location.origin}/trinity/slotboard/${publicSlug}`
    await navigator.clipboard.writeText(publicUrl)
  }

  if (isLoading) return <PulseLoader color={"#ea580c"} />

  if (isError) {
    return (
      <div className="text-sm text-orange-700">
        Error occurred: {error?.data?.message}
      </div>
    )
  }

  const userSchedulingPages =
    userSchedulingPagesData?.ids?.map(id => {
      const page = userSchedulingPagesData?.entities[id]

      return {
        _id: page?._id,
        pageName: page?.pageName,
        publicSlug: page?.publicSlug,
        pageDescription: page?.pageDescription,
        googleCalendarId: page?.googleCalendarId,
        allowedTimezones: page?.allowedTimezones || [],
        isActive: page?.isActive,
      }
    }) || []

  return (
    <div className="bg-gray-200 m-1 p-1 rounded font-poppins mx-auto">
      {/* 1. Header */}
      <div className="flex justify-between items-center m-1 p-1">
        <div className="flex justify-start items-center">
          <p className="text-2xl m-1 p-1 font-bold">SlotBoard Pages</p>

          <img
            src={addIcon}
            alt=""
            className="h-10 w-10 cursor-pointer"
            onClick={onAdd}
          />
        </div>
      </div>

      {/* 2. Empty State */}
      {userSchedulingPages?.length === 0 && (
        <div className="bg-white rounded m-1 p-4">
          <p className="text-sm text-gray-600">
            No SlotBoard pages found. Click the add button to create one.
          </p>
        </div>
      )}

      {/* 3. Page List */}
      {userSchedulingPages?.map((item, index) => (
        <div key={item._id} className="m-1 p-1 bg-white rounded">
          <div className="m-1 p-1 bg-gray-100 rounded">
            <div className="flex justify-between items-center">
              <div className="flex justify-start items-center mx-2">
                <div
                  onClick={() => handleToggleDetails(item._id)}
                  className="m-1 p-1 cursor-pointer rounded-full h-7 w-7 flex justify-center items-center hover:bg-white"
                >
                  {showDetails[item._id] ? (
                    <MinusIcon className="h-6 w-6 text-orange-600" />
                  ) : (
                    <PlusIcon className="h-4 w-4 text-orange-600" />
                  )}
                </div>

                <div className="m-1 p-1">
                  <div className="flex justify-start items-center flex-wrap">
                    <p className="font-bold">
                      {index + 1} - {item.pageName}
                    </p>

                    <p className="text-[10px] bg-orange-100 text-orange-700 rounded-full mx-2 px-2 py-1">
                      {item.isActive ? "Active" : "Inactive"}
                    </p>
                  </div>

                  <p className="text-xs text-gray-600">
                    {item.pageDescription || "No description added"}
                  </p>

                  <p className="text-[10px] text-gray-500 mt-1">
                    Public Slug: {item.publicSlug}
                  </p>
                </div>
              </div>

              <div className="flex justify-start items-center mx-2">
                <div className="rounded-full flex justify-center items-center h-6 w-6 hover:bg-white">
                  <ClipboardDocumentIcon
                    onClick={() => handleCopyPublicUrl(item.publicSlug)}
                    className="h-4 w-4 text-orange-600 cursor-pointer"
                  />
                </div>

                <div className="rounded-full flex justify-center items-center h-6 w-6 hover:bg-white">
                  <PencilSquareIcon
                    onClick={() => onUpdate(item?._id)}
                    className="h-4 w-4 text-orange-600 cursor-pointer"
                  />
                </div>

                <div className="rounded-full flex justify-center items-center h-6 w-6 hover:bg-white">
                  <TrashIcon
                    onClick={() => onDelete(item?._id)}
                    className="h-4 w-4 text-orange-600 cursor-pointer"
                  />
                </div>
              </div>
            </div>

            {/* 4. Expanded Details */}
            {showDetails[item._id] && (
              <div className="m-1 p-1 bg-gray-200 rounded">
                <div className="m-1 p-1 rounded bg-white">
                  <div className="m-1 p-1">
                    <p className="text-xs font-bold">Public URL</p>
                    <p className="text-[10px] text-orange-700 break-all">
                      {window.location.origin}/trinity/slotboard/{item.publicSlug}
                    </p>
                  </div>

                  <div className="m-1 p-1">
                    <p className="text-xs font-bold">Google Calendar</p>
                    <p className="text-[10px] text-gray-600">
                      {item.googleCalendarId || "primary"}
                    </p>
                  </div>

                  <div className="m-1 p-1">
                    <p className="text-xs font-bold">Displayed Timezones</p>

                    {item.allowedTimezones?.length > 0 ? (
                      item.allowedTimezones.map((tzItem, tzIndex) => (
                        <div
                          key={tzIndex}
                          className="rounded m-1 p-1 bg-orange-50 inline-block"
                        >
                          <p className="text-[10px] mx-1 px-1">
                            {tzItem.label} - {tzItem.timezone}
                          </p>
                        </div>
                      ))
                    ) : (
                      <p className="text-[10px] text-gray-500">
                        Default timezones will be used.
                      </p>
                    )}
                  </div>

                  <div className="m-1 p-1">
                    <p className="text-[10px] text-gray-500">
                      Use the pencil icon to update this page and manage its slots.
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      ))}
    </div>
  )
}

export default EtUserSchedulingPageList